<template>
  <div class="relative w-fit max-w-full shrink-0">
    <div ref="activator">
      <button
        type="button"
        class="site-prefs-trigger"
        :class="{ 'site-prefs-trigger--open': isOpen }"
        aria-haspopup="dialog"
        :aria-expanded="isOpen"
        :aria-label="t('common.preferences.triggerLabel')"
        @click="isOpen = !isOpen"
      >
        <template v-if="canSwitchLocale">
          <span>{{ currentLocale.toUpperCase() }}</span>
          <span
            class="site-prefs-trigger__sep"
            aria-hidden="true"
          />
        </template>
        <span>{{ preferences.currency }}</span>
        <span
          class="site-prefs-trigger__sep"
          aria-hidden="true"
        />
        <span>{{ measureShort }}</span>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="site-prefs-dropdown-root"
      >
        <div
          v-if="isMobileLayout"
          class="site-prefs-mobile-root"
          @click="isOpen = false"
        >
          <div
            class="site-prefs-mobile-panel"
            @click.stop
          >
            <SitePreferencesPanel
              key="site-prefs-sheet"
              @close="isOpen = false"
            />
          </div>
        </div>
        <div
          v-else
          ref="popover"
          class="site-prefs-popover"
          :style="floatingStyles"
          role="dialog"
          aria-modal="true"
        >
          <SitePreferencesPanel
            key="site-prefs-popover"
            @close="isOpen = false"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import SitePreferencesPanel from '@/components/SitePreferencesPanel.vue';

const { t } = useI18n();
const { currentLocale, canSwitchLocale } = useLocaleSwitch();
const preferences = usePreferencesStore();
const { isMobile: isMobileBp } = useAppBreakpoints();

const isMobileLayout = computed(() => unref(isMobileBp));

const measureShort = computed(() => (preferences.measureUnit === 'm2' ? 'm²' : 'ft²'));

const activator = ref<HTMLDivElement | null>(null);
const popover = ref<HTMLDivElement | null>(null);
const isOpen = ref(false);

const floatingOpen = computed(() => isOpen.value && !isMobileLayout.value);

const { floatingStyles } = useFloating(activator, popover, {
  placement: 'bottom-end',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(8),
    flip({ fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] }),
    shift({ padding: 8 }),
  ],
  open: floatingOpen,
});

onClickOutside(
  popover,
  () => {
    if (!isOpen.value || isMobileLayout.value) return;
    isOpen.value = false;
  },
  { ignore: [activator] },
);

watch(isOpen, (open) => {
  if (!open || isMobileLayout.value) return;
  nextTick(() => {
    const el = popover.value;
    if (!el) return;
    const focusable = el.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  });
});
</script>

<style scoped lang="scss">
@use '@/styles/variables.scss' as *;

.site-prefs-trigger {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: $black-100;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid $black-20;
  border-radius: 9999px;
  background: $white;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    opacity 0.2s ease;

  &:hover {
    border-color: $black-30;
  }

  &--open {
    color: $white;
    background: $gold-primary-100;
    border-color: $gold-primary-100;

    &:hover {
      background: $gold-primary-80;
      border-color: $gold-primary-80;
    }
  }

  &:focus-visible {
    outline: 2px solid $gold-primary-100;
    outline-offset: 2px;
  }
}

.site-prefs-trigger__flag {
  display: block;
  flex-shrink: 0;
  width: 20px;
  height: 15px;
  object-fit: cover;
  border-radius: 2px;
}

.site-prefs-trigger__sep {
  width: 1px;
  flex-shrink: 0;
  height: 14px;
  background: $black-20;
}

.site-prefs-trigger--open .site-prefs-trigger__sep {
  background: rgba(255, 255, 255, 0.45);
}

.site-prefs-popover {
  z-index: 10001;
  height: fit-content;
  max-height: min(90vh, 640px);
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 20px;
  background: $white;
  box-shadow: $box-shadow-floating;
}

.site-prefs-mobile-root {
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: rgba(0, 0, 0, 0.5);
}

.site-prefs-mobile-panel {
  position: absolute;
  inset-inline-end: 0;
  bottom: 0;
  inset-inline-start: 0;
  max-height: min(88vh, 640px);
  overflow: auto;
  border-radius: 20px 20px 0 0;
  background: $white;
  box-shadow: 0 -4px 24px rgba(15, 21, 29, 0.12);
}
</style>
